import { ContactInsight } from '../../../models/poData/ContactInsight';
import { EmployeeContact } from '../../../models/contacts/EmployeeContact';
import { employeeDisplayName } from './employee-directory.rules';
import { capitalizeNameFromEmail } from '../../../utils/extract-name-from-email';

/**
 * Classification of a PO contact, derived from its BambooHR insight.
 *
 * - `employee`       — matched a real person in BambooHR; safe to email.
 * - `internal-group` — an `@macmiller.com` address with no matching person;
 *                      almost certainly a group / distribution list.
 * - `external`       — off-domain; could be a real vendor/customer.
 * - `unknown`        — no insight available (name-valued contact, or BambooHR
 *                      was unavailable so the whole list came back empty).
 */
export type ContactClass = 'employee' | 'internal-group' | 'external' | 'unknown';

/**
 * Classifies a contact from its insight. See {@link ContactClass}.
 *
 * @param insight - The contact's BambooHR insight, or undefined when none exists.
 */
export function classifyContact(insight?: ContactInsight): ContactClass {
  if (!insight) { return 'unknown'; }
  if (insight.isEmployee) { return 'employee'; }
  if (insight.isInternal) { return 'internal-group'; }
  return 'external';
}

/**
 * Whether a contact should be *pre-checked* on the notify list by default:
 * ONLY a verified employee (`isEmployee` on its backend insight) pre-checks.
 *
 * Policy (flags set the default; the user always makes the final call):
 * - employee        → checked   (known person)
 * - internal-group  → unchecked (isInternal && !isEmployee — group / mailing
 *                                list, ado-11284: never auto-notify these)
 * - external        → unchecked (opt-in — could be a real vendor/customer)
 * - unknown         → unchecked (no insight vouching for it — the contact
 *                                stays visible for manual opt-in; with
 *                                BambooHR unavailable nothing pre-checks and
 *                                the user picks recipients explicitly)
 *
 * @param insight - The contact's BambooHR insight, or undefined when none exists.
 */
export function isDefaultChecked(insight?: ContactInsight): boolean {
  return classifyContact(insight) === 'employee';
}

/**
 * The contact's real name as BambooHR knows it, or null when we don't know it.
 *
 * Preferred name wins over the legal first name, so an employee recorded as
 * "Robert" who goes by "Bob" reads as Bob. Returns null rather than a partial
 * name when Bamboo matched no person (a group address, an external, or no
 * insight at all) so callers can tell "unknown" from "known".
 *
 * @param insight - The contact's BambooHR insight, or undefined when none exists.
 */
export function bambooDisplayName(insight?: ContactInsight): string | null {
  // Shares the directory's rule so a PO contact and a directory row can't format
  // the same person's name two different ways.
  return employeeDisplayName(insight);
}

/**
 * The name to show or print for a contact, best source first.
 *
 * 1. The employee directory (`GET /V1/employee-emails`, cached app-wide), which
 *    knows every active employee — not just the ones this PO happened to carry.
 * 2. The PO's own BambooHR insight, for a contact the directory has no row for.
 * 3. A name picked out of the address, so an unmatched contact (a group address,
 *    an external) still reads as something other than a raw address.
 *
 * The directory leads because it's the broader source: an address the user typed
 * in by hand, or one that reached us without an insight, still resolves to the
 * person's real preferred name.
 *
 * @param email - The contact's email address.
 * @param insight - The contact's BambooHR insight, or undefined when none exists.
 * @param employee - The matching directory row, or undefined when not found.
 *   Callers get this from `ContactsService.findEmployee` — or use
 *   `ContactsService.resolveName`, which passes it for you.
 * @returns A display name; '' only when there is no name and no email.
 */
export function resolveContactName(
  email: string,
  insight?: ContactInsight,
  employee?: EmployeeContact | null
): string {
  return employeeDisplayName(employee)
    ?? bambooDisplayName(insight)
    ?? capitalizeNameFromEmail(email);
}

/** A small view-model for the badge shown next to a PO contact. */
export interface ContactBadge {
  kind: ContactClass;
  /** Short colored chip text, e.g. "Employee", "Group / list", "External". */
  label: string;
  /**
   * Always-visible detail line for a matched employee — job title, department,
   * and location joined (blanks skipped), e.g. "Service Technician · Service ·
   * Denver". This is what surfaces the service-vs-project hint on touch devices
   * where a hover tooltip never shows. Empty for non-employee kinds.
   */
  meta: string;
  /** Hover tooltip (desktop): the meta line for employees, else an explanation. */
  detail: string;
}

/**
 * Builds the badge shown beside a PO contact so the user can tell a real person —
 * and their job title, department, and location (the service-vs-project hint) —
 * from a group address or an external party. Returns `null` when there's nothing
 * to show (unknown contact).
 *
 * @param insight - The contact's BambooHR insight, or undefined when none exists.
 */
export function contactInsightBadge(insight?: ContactInsight): ContactBadge | null {
  const kind = classifyContact(insight);
  switch (kind) {
    case 'employee': {
      // Job title, department, AND location all matter for the hint — show all
      // three (whichever are present), not just the title.
      const meta = [insight!.jobTitle, insight!.department, insight!.location]
        .map(v => (v || '').trim())
        .filter(Boolean)
        .join(' · ');
      return {
        kind,
        label: 'Employee',
        meta,
        detail: meta || 'Known employee',
      };
    }
    case 'internal-group':
      return { kind, label: 'Group / list', meta: '', detail: 'Internal address with no matching person — likely a group or mailing list' };
    case 'external':
      return { kind, label: 'External', meta: '', detail: 'Outside macmiller.com — opt in to notify' };
    default:
      return null;
  }
}
